/**
 * LUNA Coding Tutor - Chat Interface Component
 * 
 * 기능:
 * - 채팅 메시지 렌더링 (사용자/AI 구분)
 * - 스트리밍 타이핑 효과
 * - 마크다운 렌더링
 * - 학습 유형 선택 버튼 (LearningTypeSelector)
 * - 학습 경로 상담 버튼 (PathConsultation) [신규]
 * - 재시도 버튼 등 인터랙티브 요소
 */

import React, { useState, useEffect, useRef, useCallback, memo, forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import useLunaStore, { APP_STATES, LEARNING_TYPES } from '@/store/useLunaStore';
import { useLunaFlow } from '@/hooks/useLunaFlow';
import WelcomeScreen from '@/components/welcome/WelcomeScreen';
import { LunaAvatar, UserAvatar } from '../ui/icons/LunaIcons';
import { useTypingEffect } from '@/hooks/useTypingEffect';

// ============================================
// 채팅 메시지 컴포넌트
// ============================================

/**
 * 단일 채팅 메시지 (Memoized & ForwardRef to fix Framer Motion warnings)
 */
// [New] Lesson Completion Card Component (in Chat) - Deep Space Glass Design
const LessonCompletionCard = memo(({ onReview, onPractice, onNext, isRoadmapMode, isLastLesson }) => {
  const { username } = useLunaStore();
  const navigate = useNavigate();

  // [Guest Mode] 체험판 완료 카드
  if (!username) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="w-full max-w-md relative overflow-hidden rounded-3xl p-[1px] group"
      >
        {/* Animated Border Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/50 via-purple-500/50 to-indigo-500/50 opacity-70 group-hover:opacity-100 transition-opacity duration-700 animate-gradient-xy"></div>

        {/* Inner Content */}
        <div className="relative bg-[#0f111a] backdrop-blur-3xl rounded-[23px] p-6 h-full overflow-hidden">
          {/* Background Effects */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

          <div className="flex flex-col items-center text-center relative z-10">
            {/* Animated Icon */}
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-cyan-400 blur-[20px] opacity-20 animate-pulse-slow"></div>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="w-20 h-20 rounded-full border border-cyan-500/20 shadow-[0_0_30px_rgba(34,211,238,0.1)] flex items-center justify-center bg-slate-900/50 backdrop-blur-md"
              >
                <span className="text-4xl filter drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]">🚀</span>
              </motion.div>
            </div>

            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400 mb-3 tracking-tight">
              체험 레슨 완료!
            </h3>
            <p className="text-sm text-slate-400 font-medium leading-relaxed mb-8 max-w-[90%]">
              LUNA와 함께하는 코딩 학습이 어떠셨나요?<br />
              <span className="text-indigo-300">로그인</span>하면 <strong className="text-white">나만의 맞춤형 커리큘럼</strong>과<br />
              <strong className="text-white">실시간 AI 튜터링</strong>을 무료로 이용할 수 있어요.
            </p>

            <div className="flex flex-col gap-3 w-full">
              <button
                onClick={() => navigate('/login')}
                className="relative w-full py-3.5 px-4 rounded-xl font-bold text-white overflow-hidden group/btn transition-all hover:scale-[1.02] shadow-[0_0_20px_rgba(6,182,212,0.15)] hover:shadow-[0_0_30px_rgba(6,182,212,0.3)]"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 group-hover/btn:from-cyan-500 group-hover/btn:to-blue-500 transition-colors duration-300"></div>
                {/* Shimmer Effect */}
                <div className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

                <div className="relative flex items-center justify-center gap-2">
                  <span>로그인하고 계속하기</span>
                  <i className="fas fa-arrow-right group-hover/btn:translate-x-1 transition-transform"></i>
                </div>
              </button>

              <button
                onClick={() => navigate('/signup')}
                className="w-full py-2.5 px-4 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/80 font-medium text-xs transition-all border border-slate-700/50 hover:border-slate-500"
              >
                아직 계정이 없으신가요? 회원가입
              </button>

              <div className="my-2 border-t border-slate-800 w-2/3 mx-auto"></div>

              <button
                onClick={onReview}
                className="w-full py-2 px-4 rounded-xl text-slate-500 hover:text-cyan-400 text-xs font-medium transition-colors flex items-center justify-center gap-2 hover:bg-cyan-950/20"
              >
                <i className="fas fa-undo"></i>
                체험 레슨 복습하기
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // [User Mode] 기존 완료 카드
  const { isLoading } = useLunaStore(); // [Fix] 로딩 상태 구독

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="w-full max-w-md relative overflow-hidden rounded-3xl p-[1px] group"
    >
      {/* Border Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/60 via-purple-500/60 to-pink-500/60 opacity-80 group-hover:opacity-100 transition-opacity duration-700 animate-gradient-xy"></div>

      <div className="relative bg-[#0f111a] backdrop-blur-3xl rounded-[23px] p-8 h-full overflow-hidden">
        {/* Deep Space Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/20 via-slate-900/0 to-slate-900/0 pointers-events-none"></div>
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/20 rounded-full blur-[50px] pointer-events-none"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-indigo-500/20 rounded-full blur-[50px] pointer-events-none"></div>

        <div className="flex flex-col items-center text-center relative z-10">

          {/* Animated Success Icon */}
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-tr from-green-400 to-emerald-500 blur-[20px] opacity-30 animate-pulse"></div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2, bounce: 0.5 }}
              className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-[0_0_30px_rgba(74,222,128,0.4)] relative border-2 border-white/10"
            >
              <i className="fas fa-check text-3xl text-white drop-shadow-md"></i>
            </motion.div>
            {/* Flying Particles (Decoration) */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -top-2 -right-2 text-xl"
            >🎉</motion.div>

          </div>

          <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-sm tracking-tight">단원 학습 완료!</h3>
          <p className="text-sm text-slate-400 font-medium leading-relaxed mb-8">
            축하합니다! 학습을 성공적으로 마쳤습니다.<br />
            더 높은 목표를 향해 계속 나아가볼까요?
          </p>

          <div className="flex flex-col gap-3 w-full">
            {/* Primary Action: Next */}
            <button
              onClick={onNext}
              disabled={isLoading}
              className={`relative w-full py-4 px-4 rounded-xl font-bold text-white overflow-hidden group/btn transition-all hover:scale-[1.02] shadow-[0_4px_20px_rgba(99,102,241,0.3)] hover:shadow-[0_6px_25px_rgba(99,102,241,0.4)] ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 group-hover/btn:from-indigo-500 group-hover/btn:via-purple-500 group-hover/btn:to-pink-500 transition-all duration-300"></div>
              {/* Shimmer Effect */}
              {!isLoading && (
                <div className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/25 to-transparent"></div>
              )}

              <div className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>다음 단계 준비 중...</span>
                  </>
                ) : (
                  <>
                    {isRoadmapMode && isLastLesson ? (
                      <>
                        <span>다음 토픽으로 이동</span>
                        <i className="fas fa-rocket group-hover/btn:translate-x-1 transition-transform"></i>
                      </>
                    ) : (
                      <>
                        <span className="tracking-wide">다음 단원 시작</span>
                        <i className="fas fa-arrow-right group-hover/btn:translate-x-1 transition-transform"></i>
                      </>
                    )}
                  </>
                )}
              </div>
            </button>

            {/* Secondary Action: Practice */}
            <button
              onClick={onPractice}
              disabled={isLoading}
              className="w-full py-3.5 px-4 rounded-xl bg-slate-800/50 hover:bg-slate-700/60 text-indigo-300 hover:text-indigo-200 border border-indigo-500/20 hover:border-indigo-500/40 text-sm font-semibold transition-all flex items-center justify-center gap-2 group/practice backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-fire text-orange-500 group-hover/practice:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(249,115,22,0.5)]"></i>
              응용 문제 도전하기
            </button>

            {/* Tertiary Action: Review */}
            <button
              onClick={onReview}
              disabled={isLoading}
              className="w-full py-2.5 px-4 rounded-xl text-slate-500 hover:text-slate-300 text-xs font-medium transition-all flex items-center justify-center gap-2 hover:bg-slate-800/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-undo text-[10px]"></i>
              이번 레슨 복습하기
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

// ============================================
// [New] Typing Indicator (Bouncing Dots)
// ============================================

const TypingIndicator = memo(() => {
  const dotVariants = {
    initial: { y: 0, opacity: 0.4 },
    animate: { y: -4, opacity: 1 },
  };

  const containerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1, // Sequential delay for dots
      }
    },
    exit: { opacity: 0 }
  };

  return (
    <div className="flex items-center justify-center gap-1.5 py-1 px-1 min-w-[40px]">
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex items-center gap-1.5"
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            variants={dotVariants}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatType: "mirror",
              ease: "easeInOut"
            }}
            className="w-1.5 h-1.5 rounded-full bg-slate-400"
          />
        ))}
      </motion.div>
    </div>
  );
});

/**
 * 단일 채팅 메시지 (Memoized & ForwardRef to fix Framer Motion warnings)
 */
const ChatMessage = memo(forwardRef(function ChatMessage({ message, isStreaming = false, onAction }, ref) {
  const { role, content, timestamp, type } = message;
  const isUser = role === 'user';

  // [New] Completion Card Rendering
  if (type === 'completion_card') {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-center my-4"
      >
        <LessonCompletionCard
          onReview={onAction?.onReview}
          onPractice={onAction?.onPractice}
          onNext={onAction?.onNext}
          isRoadmapMode={onAction?.isRoadmapMode}
          isLastLesson={onAction?.isLastLesson}
        />
      </motion.div>
    );
  }

  // 4. [New] skipTyping 플래그가 없는 경우 (즉시 표시 메시지)
  // 5. [Fix] 이미 스트리밍된 메시지는 다시 타이핑하지 않음 (wasStreamed)
  const shouldAnimate = !isUser && !isStreaming && timestamp && (Date.now() - new Date(timestamp).getTime() < 5000) && !message.skipTyping && !message.wasStreamed;

  // Custom Hook 사용
  const { displayedText, isTyping } = useTypingEffect(
    typeof content === 'string' ? content : '',
    15, // 속도 (ms) - 조금 더 빠르게
    shouldAnimate
  );

  // 실제로 표시할 콘텐츠 결정
  // content가 문자열이 아니면(컴포넌트 등) 그냥 보여줌
  const contentToRender = (typeof content === 'string' && shouldAnimate) ? displayedText : (typeof content === 'string' ? content : String(content ?? ''));
  const showCursor = isTyping || (isStreaming && !isUser);

  // 마크다운 컴포넌트 커스텀 렌더러 inside ChatMessage...
  const components = {
    // ... existing components logic ...
    code({ node, inline, className, children, ...props }) {
      // ... existing code renderer ...
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : null;
      const codeContent = String(children).replace(/\n$/, '');
      const isMultiLine = codeContent.includes('\n');
      const shouldRenderAsInline = inline || (!match && !isMultiLine && codeContent.length < 100);

      if (!shouldRenderAsInline) {
        let displayLang = language;
        // [Smart Detection] If language is not specified, guess from content
        if (!displayLang || displayLang === 'text') {
          if (codeContent.includes('<!DOCTYPE') || codeContent.includes('<html') || codeContent.includes('</div>')) {
            displayLang = 'html';
          } else if (codeContent.includes('import ') || codeContent.includes('def ') || codeContent.includes('print(')) {
            displayLang = 'python';
          } else {
            displayLang = 'text';
          }
        }

        const displayTitle = displayLang === 'python' ? 'main.py' :
          displayLang === 'html' ? 'index.html' :
            displayLang === 'css' ? 'style.css' :
              displayLang === 'javascript' ? 'script.js' :
                'Code snippet';

        return (
          <div className="ide-code-block chat-ide-code-block my-3 rounded-lg overflow-hidden !bg-[#1e1e1e] border border-white/10 shadow-lg max-w-full">
            <div className="ide-header flex items-center justify-between px-3 py-1.5 !bg-[#2d2d2d] border-b border-black/20">
              <div className="flex items-center gap-2">
                <div className="ide-dots flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]"></span>
                </div>
                <span className="ide-title text-slate-400 text-xs ml-2 font-mono opacity-80">{displayTitle}</span>
              </div>
            </div>
            <div className="relative overflow-x-auto">
              <SyntaxHighlighter
                language={displayLang}
                style={vscDarkPlus}
                customStyle={{ margin: 0, padding: '1rem', fontSize: '13px', lineHeight: '1.5' }}
                wrapLongLines={false}
              >
                {codeContent}
              </SyntaxHighlighter>
            </div>
          </div>
        );
      }
      return (
        <code className="bg-slate-700 px-1.5 py-0.5 rounded text-cyan-400 text-sm font-mono" {...props}>
          {children}
        </code>
      );
    },
    // ... other components (a, strong, p, ul, ol) ...
    a({ node, ...props }) { return <a className="text-cyan-400 hover:text-cyan-300 underline" target="_blank" rel="noopener noreferrer" {...props} />; },
    strong({ node, ...props }) { return <strong className="text-white font-semibold" {...props} />; },
    ul({ node, ...props }) { return <ul className="list-disc list-inside space-y-1 my-2" {...props} />; },
    ol({ node, ...props }) { return <ol className="list-decimal list-inside space-y-1 my-2" {...props} />; },
    p({ node, ...props }) { return <div className="mb-2 last:mb-0" {...props} />; },
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        'flex gap-3 mb-4 items-start', // Reduced gap, adjusted margin, ensured top alignment
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar (Flat Design) */}
      {isUser ? <UserAvatar /> : <LunaAvatar />}

      {/* Message Bubble */}
      <motion.div
        layout // [New] Enable automatic layout animation for smooth resizing
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
        className={clsx(
          'chat-bubble relative max-w-[85%] min-w-[60px] px-4 py-3 shadow-lg',
          isUser
            ? 'bg-[#0f172a] text-white rounded-2xl rounded-tr-none' // Ultra Dark Blue (Blue-950)
            : 'bg-[#251d3a] text-white rounded-2xl rounded-tl-none' // Muted Gray-Purple (Desaturated)
        )}>
        {typeof content === 'string' ? (
          <div className={clsx("markdown-content text-sm", showCursor && "typing")}>
            <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
              {String(contentToRender)}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="text-sm">{React.isValidElement(content) ? content : String(content ?? '')}</div>
        )}

        {/* [New] Error Retry Button */}
        {type === 'error_retry' && onAction?.onRetry && (
          <button
            onClick={onAction.onRetry}
            className="mt-3 w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-bold rounded-lg transition-colors border border-red-500/30 flex items-center justify-center gap-2"
          >
            <i className="fas fa-sync-alt"></i> 다시 시도하기
          </button>
        )}

        {timestamp && (
          <div className={clsx(
            'text-[10px] mt-1 opacity-70 font-medium', // Reduced top margin
            isUser ? 'text-cyan-100 text-right' : 'text-indigo-100 text-left'
          )}>
            {new Date(timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </motion.div>
    </motion.div >
  );
}));

// ============================================
// 학습 유형 선택 버튼 (Fallback)
// ============================================

/**
 * 학습 유형 선택 카드 컴포넌트 (ForwardRef)
 */
const LearningTypeSelector = forwardRef(function LearningTypeSelector({ topic, onSelectRoadmap, onSelectCurriculum }, ref) {
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-xl mt-4"
    >
      {/* 장기 로드맵 버튼 */}
      <button
        onClick={onSelectRoadmap}
        className="group relative flex flex-col items-start gap-3 p-5 bg-gradient-to-br from-indigo-900/40 to-purple-900/30 hover:from-indigo-800/50 hover:to-purple-800/40 border border-indigo-500/30 hover:border-indigo-400/60 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 hover:-translate-y-0.5 overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
          <i className="fas fa-route text-6xl text-indigo-400 transform -rotate-12"></i>
        </div>

        <div className="relative z-10 w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center group-hover:bg-indigo-500/30 transition-colors">
          <span className="text-3xl">🗺️</span>
        </div>

        <div className="relative z-10 text-left">
          <h3 className="text-lg font-bold text-white group-hover:text-indigo-200 transition-colors mb-1">
            장기 커리어 로드맵
          </h3>
          <p className="text-sm text-slate-400 leading-relaxed font-medium">
            3~6개월 과정의 체계적인<br />
            단계별 마스터 플랜
          </p>
        </div>
      </button>

      {/* 단기 커리큘럼 버튼 */}
      <button
        onClick={onSelectCurriculum}
        className="group relative flex flex-col items-start gap-3 p-5 bg-gradient-to-br from-cyan-900/40 to-blue-900/30 hover:from-cyan-800/50 hover:to-blue-800/40 border border-cyan-500/30 hover:border-cyan-400/60 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/10 hover:-translate-y-0.5 overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
          <i className="fas fa-book-reader text-6xl text-cyan-400 transform rotate-12"></i>
        </div>

        <div className="relative z-10 w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
          <span className="text-3xl">📚</span>
        </div>

        <div className="relative z-10 text-left">
          <h3 className="text-lg font-bold text-white group-hover:text-cyan-200 transition-colors mb-1">
            단기 핵심 커리큘럼
          </h3>
          <p className="text-sm text-slate-400 leading-relaxed font-medium">
            빠르게 핵심만 학습하고<br />
            즉시 실습하는 속성 코스
          </p>
        </div>
      </button>
    </motion.div>
  );
});

// ============================================
// [신규] 학습 경로 상담 (Consultation)
// ============================================

/**
 * 학습 경로 상담 카드 컴포넌트
 * AI가 제안한 단기/장기 경로를 단일 목록으로 표시합니다.
 * 
 * [개선] 탭 분리 제거 - 항상 단일 구조
 * - learned_acknowledgment가 있으면 (토픽 마스터 완료 시) 인정 멘트 표시
 * - 각 옵션의 description에서 "이전 학습 활용" vs "새로 시작" 여부를 자연스럽게 표현
 */
const PathConsultation = forwardRef(function PathConsultation({ data, onSelectShort, onSelectMedium, onSelectLong, onRegenerate }, ref) {
  if (!data) return null;

  const {
    comment,
    learned_acknowledgment,
    short_term,
    medium_term, // [신규]
    long_term
  } = data;

  // ========== 아이콘 렌더링 헬퍼 함수 ==========
  const renderIcon = (iconName, defaultIcon) => {
    if (!iconName) return <span className="text-xl">{defaultIcon}</span>;

    let iconClass = iconName.trim();

    // "fa-book" 처럼만 들어온 경우 -> "fas fa-book"으로 변환
    if (iconClass.startsWith('fa-') && !iconClass.includes(' ')) {
      iconClass = `fas ${iconClass}`;
    }
    // "book" 처럼만 들어온 경우 -> "fas fa-book"으로 변환
    else if (!iconClass.startsWith('fa') && !iconClass.includes(' ')) {
      iconClass = `fas fa-${iconClass}`;
    }

    return <i className={`${iconClass} text-xl`} />;
  };

  // ========== 옵션 렌더링 헬퍼 함수 ==========
  const renderOptionButton = (opt, idx, type, onSelect) => {
    // 색상 테마 정의
    const themes = {
      short: {
        bg: 'from-cyan-900/30 to-blue-900/20 hover:from-cyan-800/40 hover:to-blue-800/30',
        border: 'border-cyan-500/30 hover:border-cyan-400/50',
        iconBg: 'bg-cyan-500/20 text-cyan-400',
        badge: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/20',
        titleHover: 'group-hover:text-cyan-300',
        badgeText: '단기 완성'
      },
      medium: { // [신규] Emerald Theme
        bg: 'from-emerald-900/30 to-teal-900/20 hover:from-emerald-800/40 hover:to-teal-800/30',
        border: 'border-emerald-500/30 hover:border-emerald-400/50',
        iconBg: 'bg-emerald-500/20 text-emerald-400',
        badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20',
        titleHover: 'group-hover:text-emerald-300',
        badgeText: '과목 마스터'
      },
      long: {
        bg: 'from-indigo-900/30 to-purple-900/20 hover:from-indigo-800/40 hover:to-purple-800/30',
        border: 'border-indigo-500/30 hover:border-indigo-400/50',
        iconBg: 'bg-indigo-500/20 text-indigo-400',
        badge: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/20',
        titleHover: 'group-hover:text-indigo-300',
        badgeText: '커리어 로드맵'
      }
    };

    const theme = themes[type] || themes.long;

    return (
      <button
        key={`${type}-${idx}`}
        onClick={() => onSelect(opt.title || '')}
        className={`w-full group flex items-start gap-4 p-4 rounded-xl transition-all text-left bg-gradient-to-r ${theme.bg} border ${theme.border}`}
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform ${theme.iconBg}`}>
          {renderIcon(opt.icon, type === 'short' ? '📚' : type === 'medium' ? '🎓' : '🚀')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${theme.badge}`}>
              {theme.badgeText}
            </span>
            <h4 className={`text-sm font-bold text-white transition-colors ${theme.titleHover}`}>
              {opt.title}
            </h4>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            {opt.description}
          </p>
        </div>
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity self-center">
          <i className={`fas fa-chevron-right ${type === 'short' ? 'text-cyan-400' : type === 'medium' ? 'text-emerald-400' : 'text-indigo-400'}`}></i>
        </div>
      </button>
    );
  };

  // ========== 단일 구조 UI (탭 분리 제거) ==========
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="w-full max-w-lg mt-3 bg-slate-800/80 rounded-2xl border border-slate-700/50 overflow-hidden shadow-xl backdrop-blur-sm"
    >
      {/* 상담 멘트 */}
      <div className="p-4 border-b border-slate-700/50 bg-slate-900/40">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 text-sm">
            🌙
          </div>
          <div>
            <p className="text-sm text-slate-200 leading-relaxed">
              {comment || '학습 방향을 고민해봤어요. 어떤 방식으로 시작할까요?'}
            </p>
            {/* 토픽 마스터 완료 시 인정 멘트 표시 */}
            {learned_acknowledgment && (
              <p className="text-xs text-emerald-400/80 mt-1.5 flex items-center gap-1">
                <i className="fas fa-check-circle"></i>
                {learned_acknowledgment}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* 단기 커리큘럼 제안 목록 */}
        {short_term && short_term.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-semibold text-cyan-400/80 px-1">단기 학습 추천</h5>
            {short_term.map((opt, idx) => renderOptionButton(opt, idx, 'short', onSelectShort))}
          </div>
        )}

        {/* [신규] 중기 마스터 과정 목록 */}
        {medium_term && medium_term.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-slate-700/50">
            <h5 className="text-xs font-semibold text-emerald-400/80 px-1">중기 과목 마스터</h5>
            {medium_term.map((opt, idx) => renderOptionButton(opt, idx, 'medium', onSelectMedium))}
          </div>
        )}

        {/* 장기 로드맵 제안 목록 */}
        {long_term && long_term.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-slate-700/50">
            <h5 className="text-xs font-semibold text-indigo-400/80 px-1">장기 커리어 로드맵</h5>
            {long_term.map((opt, idx) => renderOptionButton(opt, idx, 'long', onSelectLong))}
          </div>
        )}

        {/* 다른 방향 제안하기 */}
        <button
          onClick={onRegenerate}
          className="w-full py-2 flex items-center justify-center gap-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          <i className="fas fa-sync-alt"></i> 다른 방향으로 추천받기
        </button>
      </div>
    </motion.div>
  );
});


// ============================================
// 주제 선택 버튼 그룹
// ============================================

/**
 * 세부 주제 선택 버튼 그룹 (ForwardRef)
 */
const TopicOptionsSelector = forwardRef(function TopicOptionsSelector({ topics, onSelect }, ref) {
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-lg mt-3"
    >
      {topics.map((opt, idx) => {
        const isObj = opt && typeof opt === 'object' && !Array.isArray(opt);
        const title = isObj ? (opt.title || '') : String(opt || '');
        const desc = isObj ? (opt.description || '') : '';

        return (
          <button
            key={idx}
            onClick={() => onSelect(title)}
            className="text-left bg-slate-700/50 hover:bg-slate-600/70 border border-slate-600 hover:border-cyan-500/50 py-3 px-4 rounded-lg transition-all duration-200"
          >
            <div className="text-sm font-semibold text-white">{title}</div>
            {desc && (
              <div className="text-xs text-slate-400 mt-1">{desc}</div>
            )}
          </button>
        );
      })}
    </motion.div>
  );
});

// [신규] 로드맵 카드 (로드맵 생성 후 표시)
const RoadmapCard = forwardRef(function RoadmapCard({ roadmap, onViewDetail, onRegenerate, onStart }, ref) {
  if (!roadmap) return null;

  const phases = roadmap.phases || [];
  const totalTopics = phases.reduce((sum, p) => sum + (p.topics?.length || 0), 0);
  const isCourse = roadmap.type === 'course';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        w-full max-w-md rounded-2xl overflow-hidden shadow-2xl backdrop-blur-sm
        ${isCourse
          ? 'bg-gradient-to-br from-emerald-900/90 to-teal-900/90 border border-emerald-500/30'
          : 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-slate-700/50'}
      `}
    >
      {/* Header */}
      <div className={`p-4 border-b ${isCourse ? 'border-emerald-500/30 bg-gradient-to-r from-emerald-900/30 to-teal-900/20' : 'border-slate-700/50 bg-gradient-to-r from-indigo-900/30 to-purple-900/20'}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <span className={`inline-block px-2 py-0.5 mb-2 text-[10px] font-bold rounded border ${isCourse ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'}`}>
              {isCourse ? 'MASTER COURSE GENERATED' : 'ROADMAP GENERATED'}
            </span>
            <h3 className="text-base font-bold text-white leading-tight">
              {roadmap.roadmap_title || '마스터 로드맵'}
            </h3>
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
              {roadmap.description}
            </p>
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ml-3 ${isCourse ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-indigo-500 to-purple-600'}`}>
            <i className={`fas ${isCourse ? 'fa-graduation-cap' : 'fa-route'} text-white`}></i>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <i className="fas fa-layer-group text-indigo-400"></i>
            {phases.length} Phases
          </span>
          <span className="flex items-center gap-1">
            <i className="fas fa-tasks text-cyan-400"></i>
            {totalTopics} Topics
          </span>
          <span className="flex items-center gap-1">
            <i className="fas fa-clock text-emerald-400"></i>
            {roadmap.estimated_duration || '3-6개월'}
          </span>
        </div>
      </div>

      {/* Phases Preview */}
      <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
        {phases.slice(0, 4).map((phase, idx) => (
          <div
            key={idx}
            className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/50 border border-slate-700/30"
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${idx === 0 ? 'bg-cyan-500 text-white' : 'bg-slate-700 text-slate-400'
              }`}>
              {phase.icon ? <i className={`fas ${phase.icon}`}></i> : idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">Phase {idx + 1}</span>
                {idx === 0 && <span className="text-[9px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">시작</span>}
              </div>
              <h4 className="text-xs font-medium text-slate-200 truncate">{phase.title}</h4>
            </div>
            <span className="text-[10px] text-slate-500 shrink-0">{phase.topics?.length || 0} topics</span>
          </div>
        ))}
        {phases.length > 4 && (
          <div className="text-center text-[10px] text-slate-500 py-1">
            +{phases.length - 4} more phases...
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 border-t border-slate-700/50 bg-slate-900/50 flex gap-2">
        <button
          onClick={onViewDetail}
          className="flex-1 py-2 px-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors border border-slate-600/50 flex items-center justify-center gap-1.5"
        >
          <i className="fas fa-search"></i> 상세 보기
        </button>
        <button
          onClick={onRegenerate}
          className="py-2 px-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors border border-slate-600/50 flex items-center justify-center gap-1.5"
        >
          <i className="fas fa-sync-alt"></i> 재생성
        </button>
        <button
          onClick={onStart}
          className="flex-1 py-2 px-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold transition-all shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-1.5"
        >
          <i className="fas fa-rocket"></i> 시작하기
        </button>
      </div>
    </motion.div>
  );
});

// [신규] 커리큘럼 카드 (커리큘럼 생성 후 표시)
const CurriculumCard = forwardRef(function CurriculumCard({ curriculum, onStepClick }, ref) {
  if (!curriculum) return null;

  const steps = curriculum.curriculum || [];
  const topic = curriculum.topic || '학습 주제';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl backdrop-blur-sm"
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-700/50 bg-gradient-to-r from-cyan-900/30 to-blue-900/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
            <i className="fas fa-book-open text-white"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white leading-tight">{topic}</h3>
            <p className="text-xs text-slate-400 mt-1">{steps.length}개의 학습 단계</p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
        {steps.map((step, idx) => (
          <button
            key={idx}
            onClick={() => onStepClick(idx)}
            className="w-full flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/30 hover:border-cyan-500/30 transition-all text-left group"
          >
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs font-bold shrink-0 group-hover:bg-cyan-500 group-hover:text-white transition-colors">
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-medium text-slate-200 group-hover:text-cyan-300 transition-colors line-clamp-1">
                {step.title}
              </h4>
              <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">
                {step.description}
              </p>
            </div>
            <i className="fas fa-chevron-right text-slate-600 group-hover:text-cyan-400 text-xs transition-colors"></i>
          </button>
        ))}
      </div>
    </motion.div>
  );
});

// [유지] 간단 액션 버튼 (폴백용)
const GenerationAction = forwardRef(function GenerationAction({ type, onStart, onRegenerate }, ref) {
  const isRoadmap = type === 'roadmap';
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-3 mt-3 w-full max-w-lg"
    >
      <button
        onClick={onStart}
        className={`flex-1 py-3 px-4 rounded-xl font-bold text-white shadow-lg transition-all transform hover:scale-105 ${isRoadmap
          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-indigo-500/30'
          : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:shadow-cyan-500/30'
          }`}
      >
        <div className="flex items-center justify-center gap-2">
          <span>{isRoadmap ? '🚀 로드맵 시작하기' : '📖 학습 시작하기'}</span>
        </div>
      </button>
      <button
        onClick={onRegenerate}
        className="px-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 text-slate-300 font-medium transition-colors border border-slate-600"
      >
        <i className="fas fa-sync-alt mr-2"></i>다시 생성
      </button>
    </motion.div>
  );
});

// ============================================



// ============================================
// 채팅 입력 폼
// ============================================

function ChatInput({ onSubmit, disabled, placeholder, locked, lockedMessage }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || disabled || locked) return;

    onSubmit(input.trim());
    setInput('');
  };

  // 자동 포커스
  useEffect(() => {
    if (!disabled && !locked) {
      inputRef.current?.focus();
    }
  }, [disabled, locked]);

  return (
    <form onSubmit={handleSubmit} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        disabled={disabled || locked}
        placeholder={locked ? lockedMessage : (placeholder || '질문을 입력하세요...')}
        className={clsx(
          'w-full bg-slate-800 text-sm text-white rounded-lg pl-3 pr-10 py-2.5 border focus:outline-none transition-colors',
          locked || disabled
            ? 'border-slate-700 opacity-50 cursor-not-allowed placeholder-slate-600'
            : 'border-slate-700 focus:border-purple-500 placeholder-slate-500'
        )}
      />
      <button
        type="submit"
        disabled={disabled || locked || !input.trim()}
        className={clsx(
          'absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded flex items-center justify-center transition-colors',
          disabled || locked || !input.trim()
            ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
            : 'bg-purple-600 hover:bg-purple-500 text-white'
        )}
      >
        <i className="fas fa-paper-plane text-xs" />
      </button>
    </form>
  );
}

// ============================================
// 메인 ChatInterface 컴포넌트
// ============================================

/**
 * 채팅 인터페이스 메인 컴포넌트
 */
export default function ChatInterface() {
  const chatLogRef = useRef(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showLearningTypeSelector, setShowLearningTypeSelector] = useState(false);
  const [showTopicOptions, setShowTopicOptions] = useState(null);
  const [pendingTopic, setPendingTopic] = useState('');

  // [신규] 상담 데이터 상태
  const [consultationData, setConsultationData] = useState(null);

  // [Fix] 채팅 잠금 상태를 로컬 상태로 관리
  // Zustand의 subscribe를 사용하여 상태 변경을 직접 감지 (리렌더링 보장)
  const [localInputLocked, setLocalInputLocked] = useState(() => useLunaStore.getState().chatInputLocked);
  const [localPlaceholder, setLocalPlaceholder] = useState(() => useLunaStore.getState().chatInputPlaceholder || '');

  // Zustand subscribe로 상태 변경 직접 감지
  useEffect(() => {
    const unsubscribe = useLunaStore.subscribe(
      (state) => {
        console.log('[ChatInterface] subscribe - chatInputLocked 변경:', state.chatInputLocked);
        setLocalInputLocked(state.chatInputLocked);
        setLocalPlaceholder(state.chatInputPlaceholder || '');
      }
    );
    return () => unsubscribe();
  }, []);


  // Store (나머지 상태들)
  const {
    chatHistory,
    isLoading,
    loadingMessage,
    appState,
    addChatMessage, // 직접 메시지 추가용
    setAppState,    // 상태 변경용
    setChatInputLocked, // 잠금 해제용
    chatInputValue, // Global Input State
    setChatInputValue, // Global Input Setter
    intendedTopic, // [신규] 상태 필요
    currentRoadmap, // [신규] 로드맵 카드 표시용
    currentCurriculum, // [신규] 커리큘럼 카드 표시용
    setCurrentRoadmap, // [신규] 로드맵 초기화용
    username, // [New] 사용자 이름 (로그인 여부 확인용)
  } = useLunaStore();

  // [New] Empty Chat Welcome Message
  useEffect(() => {
    if (!isLoading && chatHistory.length === 0 && username) {
      addChatMessage('ai', '안녕하세요! 코딩 튜터 루나입니다. 🌕\n오늘 어떤 내용을 공부해볼까요?');
    }
  }, [isLoading, chatHistory.length, username, addChatMessage]);

  // Flow Hook
  const {
    handleChatSubmit,
    selectRoadmapLearning,
    selectCurriculumLearning,
    generateCurriculum,
    generateRoadmap, // 로드맵 생성 함수 추가
    startLesson, // [신규] 레슨 시작 함수
    requestChallenge, // [New] 응용 과제 요청
    goToNextStep, // [New] 다음 단계 이동
    handleStepClick, // [Fix] Hook 규칙 위반 해결을 위해 추출
    saveCurrentState, // [New] 상태 저장용
  } = useLunaFlow();

  // [Removed] needsCurriculumGeneration useEffect는 App.jsx (LunaMainPage)에서 처리
  // ChatInterface에서도 감지하면 generateCurriculum이 이중 호출되므로 제거


  const navigate = useNavigate();

  const handleStartLearning = useCallback(async () => {
    if (appState === APP_STATES.ROADMAP_GENERATED) {
      navigate('/roadmap');
    } else if (appState === APP_STATES.CURRICULUM_GENERATED) {
      // 커리큘럼 시작
      const topic = intendedTopic || 'Coding';
      const curr = useLunaStore.getState().currentCurriculum;
      const firstStep = curr?.curriculum?.[0];
      const stepTitle = firstStep?.title || 'Basic';
      const result = await startLesson(topic, stepTitle);

      // [Fix] 레슨 생성 성공 시 상태 전환
      if (result?.success) {
        setIsStreaming(false); // [Fix] 스트리밍 상태 초기화
        setTimeout(() => setIsStreaming(false), 50); // [Safety]
        setChatInputLocked(false);
        setAppState('CONCEPT_VIEW');
      }
    }
  }, [appState, navigate, intendedTopic, startLesson, setAppState, setChatInputLocked]);

  // 채팅 로그 자동 스크롤용 Ref
  const textareaRef = useRef(null);
  // [Fix] 스크롤 고정 상태를 컴포넌트 레벨에서 관리
  const isStickToBottomRef = useRef(true);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
    }
  };

  const handleInputChange = (e) => {
    setChatInputValue(e.target.value);
    // adjustTextareaHeight is now handled by useEffect
  };

  // [Fix] chatInputValue 변경 시 자동으로 높이 조절 (외부 입력 대응)
  useEffect(() => {
    adjustTextareaHeight();
  }, [chatInputValue]);

  // [Improved] 채팅 로그 자동 스크롤 (MutationObserver 사용)
  // 타이핑 효과나 스트리밍으로 인해 콘텐츠 높이가 동적으로 변할 때도 스크롤을 하단에 유지
  useEffect(() => {
    const chatLog = chatLogRef.current;
    if (!chatLog) return;

    // 사용자 스크롤 의도 감지
    // (컴포넌트 레벨 useRef인 isStickToBottomRef 사용)

    const handleScroll = () => {
      if (!chatLog) return;
      const { scrollTop, scrollHeight, clientHeight } = chatLog;
      // 하단에서 50px 이내면 "하단 고정 모드"로 간주
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      isStickToBottomRef.current = isAtBottom;
    };

    chatLog.addEventListener('scroll', handleScroll);

    // 1. 초기 로드 및 의존성 변경 시: 하단 고정 모드면 스크롤 이동
    if (isStickToBottomRef.current) {
      chatLog.scrollTop = chatLog.scrollHeight;
    }

    // 2. DOM 변화 감지 (타이핑, 스트리밍, 애니메이션 등)
    let rafId;
    const observer = new MutationObserver((mutations) => {
      // [Fix] 단순 스타일 변경(애니메이션)은 무시
      const isStructureChange = mutations.some(m => m.type === 'childList' || m.type === 'characterData');
      if (!isStructureChange) return;

      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        // 사용자가 스크롤을 올려서 보고 있는 중(StickToBottom이 false)이라면
        // 강제로 내리지 않음. 오직 하단에 있을 때만 계속 하단 유지.
        if (isStickToBottomRef.current) {
          // [Double Check] 실제 높이가 변했을 때만 이동
          chatLog.scrollTop = chatLog.scrollHeight;
        }
        rafId = null;
      });
    });

    observer.observe(chatLog, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: false // [Fix] 스타일(애니메이션) 변경 감지 제외
    });

    return () => {
      chatLog.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [chatHistory, streamingContent, isLoading, consultationData]);

  // [Fix] 전역 안전장치: 앱이 초기화(IDLE)되거나 채팅이 비워지면 스트리밍 상태 강제 해제
  // "새 주제" 버튼 등 외부에서 resetApp()이 호출될 때 isStreaming이 true로 남는 문제 해결
  useEffect(() => {
    if (appState === APP_STATES.IDLE || chatHistory.length === 0) {
      if (isStreaming) {
        console.log('🛡️ [Auto-Fix] 초기화 감지되어 스트리밍 강제 종료');
        setIsStreaming(false);
        // 확실하게 하기 위해 한번 더
        setTimeout(() => setIsStreaming(false), 50);
      }
    }
  }, [appState, chatHistory, isStreaming]);

  // 채팅 제출 핸들러
  const handleSubmit = useCallback(async (userInput) => {
    // [Fix] 입력창 초기화 및 높이 리셋
    setChatInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '48px';
    }

    setStreamingContent('');
    setIsStreaming(true);
    setConsultationData(null); // 상담창 초기화

    // 메시지 전송 직전에 상담 데이터가 남아있다면 초기화
    if (appState === APP_STATES.AWAITING_PATH_SELECTION) {
      // 이미 선택 대기중인데 채팅을 쳤다면? lock이 걸려있을텐데...
      // 강제로 풀린 경우가 아니라면 여기 올일은 없지만 안전장치.
    }

    const result = await handleChatSubmit(userInput, {
      // 스트리밍 청크 콜백
      onChunk: (chunk) => {
        setStreamingContent((prev) => prev + chunk);
      },
      // 완료 콜백
      onComplete: (fullText) => {
        setStreamingContent('');
        setIsStreaming(false);
        // [Fix] wasStreamed 플래그는 useLunaFlow.js의 addChatMessage에서 이미 설정됨.
        // 여기서는 로컬 스트리밍 상태만 해제하면 됨.
      },
      // 학습 유형 선택 필요 콜백 (Fallback)
      onLearningTypeRequired: (topic) => {
        setPendingTopic(topic);
        setShowLearningTypeSelector(true);
        addChatMessage({
          role: 'ai',
          type: 'learning_type_card',
          content: topic
        });
      },
      // 주제 옵션 콜백
      onTopicOptions: (topics) => {
        setShowTopicOptions(topics);
        addChatMessage({
          role: 'ai',
          type: 'topic_options_card',
          content: topics
        });
      },
      // [신규] 학습 경로 상담 콜백
      onConsultation: (data) => {
        setConsultationData(data);
        addChatMessage({
          role: 'ai',
          type: 'path_selection_card',
          content: data
        });
      }
    });

    // 결과 처리
    if (result?.isBroad && result?.suggestedTopics) {
      setShowTopicOptions(result.suggestedTopics);
    }

    if (!result?.success && !result?.locked) {
      setIsStreaming(false);
    }
  }, [handleChatSubmit, appState, setChatInputValue]);

  // 학습 유형 선택 핸들러 (Fallback)
  const handleSelectRoadmap = useCallback(async () => {
    setShowLearningTypeSelector(false);
    await selectRoadmapLearning();
  }, [selectRoadmapLearning]);

  const handleSelectCurriculum = useCallback(async () => {
    setShowLearningTypeSelector(false);
    await selectCurriculumLearning();
  }, [selectCurriculumLearning]);

  // 세부 주제 선택 핸들러
  const handleSelectTopic = useCallback(async (topic) => {
    setShowTopicOptions(null);
    // addChatMessage는 handleChatSubmit 내부에서 호출되므로 중복 제거
    // addChatMessage('user', topic); 

    // [Fix] 콜백을 전달해야 상담 데이터(consultationData)를 받을 수 있음
    await handleChatSubmit(topic, {
      onConsultation: (data) => setConsultationData(data),
      onTopicOptions: (topics) => setShowTopicOptions(topics)
    });
  }, [handleChatSubmit]);

  // [Helper] 스크롤 강제 하단 이동
  const forceScrollToBottom = useCallback(() => {
    isStickToBottomRef.current = true;
    setTimeout(() => {
      if (chatLogRef.current) {
        chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
      }
    }, 50); // State update & render delay
  }, []);

  // [신규] 상담 선택 핸들러
  const handleConsultShort = useCallback(async (title) => {
    // [Fix] Side Quest Check
    const store = useLunaStore.getState();
    if (store.isRoadmapMode) {
      if (window.confirm(`🗺️ 현재 로드맵을 잠시 멈추고 "${title}" 사이드 퀘스트를 진행하시겠습니까?`)) {
        store.suspendRoadmap(); // 로드맵 일시 정지
      } else {
        return; // 취소
      }
    }

    setConsultationData(null);
    forceScrollToBottom(); // [UX] 선택 시 하단으로 스크롤 이동
    addChatMessage('user', `"${title}" 커리큘럼으로 시작할래!`);
    await generateCurriculum(title);
  }, [generateCurriculum, addChatMessage, forceScrollToBottom]);

  const handleConsultMedium = useCallback(async (title) => {
    // [Fix] Roadmap Replacement Check
    const store = useLunaStore.getState();
    if (store.isRoadmapMode) {
      if (!window.confirm(`⚠️ 기존 로드맵을 "${title}" 마스터 과정으로 교체하시겠습니까?`)) {
        return;
      }
    }

    setConsultationData(null);
    forceScrollToBottom(); // [UX] 선택 시 하단으로 스크롤 이동
    addChatMessage('user', `"${title}" 마스터 과정을 시작할래!`);
    await generateRoadmap(title, 'course');
  }, [generateRoadmap, addChatMessage, forceScrollToBottom]);

  const handleConsultLong = useCallback(async (title) => {
    // [Fix] Roadmap Replacement Check
    const store = useLunaStore.getState();
    if (store.isRoadmapMode) {
      if (!window.confirm(`⚠️ 새로운 로드맵을 시작하면 기존 로드맵은 종료됩니다.\n"${title}" 로드맵으로 교체하시겠습니까?`)) {
        return;
      }
    }

    setConsultationData(null);
    forceScrollToBottom(); // [UX] 선택 시 하단으로 스크롤 이동
    addChatMessage('user', `"${title}" 로드맵을 만들어줘!`);
    await generateRoadmap(title);
  }, [generateRoadmap, addChatMessage, forceScrollToBottom]);

  const handleConsultRegenerate = useCallback(() => {
    console.log('🔄 [handleConsultRegenerate] 호출됨');
    console.log('   - 변경 전 chatInputLocked:', useLunaStore.getState().chatInputLocked);

    // 1. 로컬 상태 먼저 초기화 (리렌더링 트리거)
    setConsultationData(null);
    setIsStreaming(false);
    // [Safety] 렌더링 사이클 후 다시 켜지는 것 방지
    setTimeout(() => setIsStreaming(false), 50);

    // 2. Store 상태 변경 (컴포넌트에서 가져온 함수 사용)
    setChatInputLocked(false);
    setAppState(APP_STATES.IDLE);
    addChatMessage('ai', '어떤 점을 더 보완할까요? 구체적으로 말씀해주세요! 💡\n\n예시:\n- "웹 개발 쪽으로 더 알려줘"\n- "입문자 수준으로"\n- "실무 위주로 배우고 싶어"');

    // 변경 후 값 확인 (비동기적으로 확인)
    setTimeout(() => {
      console.log('   - 변경 후 chatInputLocked:', useLunaStore.getState().chatInputLocked);
    }, 100);
    console.log('✅ [handleConsultRegenerate] 상태 초기화 완료');
  }, [setChatInputLocked, setAppState, addChatMessage]);

  // [신규] 로드맵 카드 액션 핸들러
  const handleRoadmapViewDetail = useCallback(() => {
    navigate('/roadmap');
  }, [navigate]);

  const handleRoadmapStart = useCallback(async () => {
    // 로드맵의 첫 번째 Phase, 첫 번째 Topic으로 커리큘럼 생성
    if (!currentRoadmap?.phases?.[0]?.topics?.[0]) {
      addChatMessage('ai', '로드맵에 토픽이 없어요. 다시 생성해주세요!');
      return;
    }

    const phase = currentRoadmap.phases[0];
    const topic = phase.topics[0];

    // 카드 숨김
    setCurrentRoadmap(null);
    setChatInputLocked(false);

    // [Fix] 로드맵 모드 활성화 및 컨텍스트 명시적 설정
    // generateCurriculum 호출 전에 컨텍스트가 설정되어 있어야 API 호출 시 포함됨
    const store = useLunaStore.getState();
    const totalTopics = currentRoadmap.phases.reduce((acc, p) => acc + (p.topics?.length || 0), 0);

    const context = {
      roadmapTitle: currentRoadmap.roadmap_title,
      career_goal: currentRoadmap.career_goal || '',
      type: currentRoadmap.type || 'career',
      pIdx: 0,
      tIdx: 0,
      topicId: topic.id,
      totalPhases: currentRoadmap.phases.length,
      totalTopics: totalTopics
    };

    console.log('[ChatInterface] Starting Roadmap with Context:', context);

    // [Optimization] 이미 로드맵 모드라면 컨텍스트만 업데이트하여 사이드바 깜빡임 방지
    if (store.isRoadmapMode) {
      store.setRoadmapContext(context);
    } else {
      store.setRoadmapMode(true, context);
    }

    // [Fix] 커리큘럼 생성 시 로드맵 모드가 초기화되지 않도록 플래그 설정
    // useLunaFlow.js의 generateCurriculum에서 이 플래그를 확인하여 로드맵 유지를 결정함
    store.setNeedsCurriculumGeneration(true);

    // 사이드바에 현재 토픽 표시 메시지
    addChatMessage('ai', `📚 **${currentRoadmap.roadmap_title}** ✨에서 **${topic.title}** 학습을 시작합니다!\n이 토픽을 완료하면 다음 토픽이 자동으로 열려요.`);

    // [Fix] 커리큘럼 생성은 App.jsx의 useEffect에서 needsCurriculumGeneration 플래그를 보고 처리함
    // 여기서 직접 호출하면 중복 생성 및 로드맵 초기화 레이스 컨디션이 발생하므로 제거함

    // [Fix] 상태 즉시 저장 (새로고침 시 인덱스 회귀 방지)
    saveCurrentState();
  }, [currentRoadmap, setCurrentRoadmap, addChatMessage, setChatInputLocked, generateCurriculum, saveCurrentState]);

  const handleRoadmapRegenerate = useCallback(() => {
    console.log('🔄 [handleRoadmapRegenerate] 호출됨');

    // Store 상태 변경 (컴포넌트에서 가져온 함수 사용)
    setCurrentRoadmap(null);
    useLunaStore.getState().setPendingRegenerateType('roadmap'); // [Fix] 재생성 타입 명시

    setIsStreaming(false);
    // [Safety] 렌더링 사이클 후 다시 켜지는 것 방지
    setTimeout(() => setIsStreaming(false), 50);

    setChatInputLocked(false);

    // [Fix] IDLE 대신 재생성 대기 상태로 변경하여 handleChatSubmit이 올바르게 분기하도록 함
    setAppState(APP_STATES.AWAITING_REGENERATE_INPUT);

    addChatMessage('ai', '어떤 점을 더 보완할까요? 구체적으로 말씀해주세요! 💡');

    console.log('✅ [handleRoadmapRegenerate] 상태 초기화 완료');
  }, [setCurrentRoadmap, setChatInputLocked, setAppState, addChatMessage]);

  // [신규] 커리큘럼 카드 Step 클릭 핸들러
  const handleCurriculumStepClick = useCallback(async (stepIndex) => {
    // [Fix] Hook 규칙 준수: 상위에서 추출한 handleStepClick 사용
    const result = await handleStepClick(stepIndex);

    // [Fix] 레슨 생성 성공 시 상태 전환
    if (result?.success) {
      setChatInputLocked(false);
      setAppState('CONCEPT_VIEW');
    }
  }, [setAppState, setChatInputLocked]);

  // [New] Completion Handlers
  const handleReview = useCallback(() => {
    // 레슨 복습: 첫 단계로 이동
    useLunaStore.getState().setLessonStepIndex(0);
    useLunaStore.getState().setLessonCompleted(false);
    setAppState('CONCEPT_VIEW');
  }, [setAppState]);

  const handlePractice = useCallback(() => {
    // 응용 문제 풀기
    requestChallenge();
  }, [requestChallenge]);

  const handleNextLesson = useCallback(async () => {
    console.log('[ChatInterface] handleNextLesson Clicked. RoadmapMode:', useLunaStore.getState().isRoadmapMode);
    const state = useLunaStore.getState();

    // [Fix] 로드맵 모드 여부와 상관없이 먼저 다음 단계를 시도합니다.
    // goToNextStep이 완료 여부와 로드맵 이동 필요 여부를 반환합니다.
    state.setLessonCompleted(false);

    // goToNextStep이 {isComplete, shouldNavigateToRoadmap, ... }을 반환한다고 가정
    const result = await goToNextStep();

    // 과정이 모두 끝났거나, 명시적으로 로드맵 이동이 필요한 경우
    if (result && result.shouldNavigateToRoadmap) {
      navigate('/roadmap');
    }
  }, [goToNextStep, navigate]);

  const isLastLesson = useLunaStore((state) => {
    const curr = state.currentCurriculum;
    const idx = state.currentStepIndex;
    if (!curr?.curriculum) return false;
    return idx >= curr.curriculum.length - 1;
  });

  // [New] Retry Handler
  const handleRetry = useCallback(async (msg) => {
    const { retryContext } = msg;
    if (!retryContext) return;

    const { type, topic } = retryContext;

    if (type === 'roadmap' && (retryContext.goal || topic)) {
      const g = retryContext.goal || topic;
      addChatMessage('user', `"${g}" 로드맵 다시 만들어줘!`);
      useLunaStore.getState().setIntendedTopic(g);
      await generateRoadmap(g, retryContext.roadmap_type || 'career');
    } else if (type === 'curriculum' && topic) {
      addChatMessage('user', `"${topic}" 다시 시도해줘!`);
      useLunaStore.getState().setIntendedTopic(topic);
      await generateCurriculum(topic);
    } else {
      console.warn('Unknown retry context:', retryContext);
    }
  }, [addChatMessage, generateCurriculum]);

  const isRoadmapMode = useLunaStore((state) => state.isRoadmapMode);

  return (
    <div className="flex flex-col h-full bg-black/40 backdrop-blur-none rounded-2xl overflow-hidden relative border-0 shadow-none">
      {/* 배경 장식 제거 (Clean) */}

      {/* 채팅 헤더 - Minimalist */}
      <div className="flex items-center justify-between px-6 py-4 bg-transparent z-10 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center p-0.5 overflow-hidden">
            <img
              src="/luna_avatar.png"
              alt="AI Luna"
              className="w-full h-full object-cover rounded-full scale-125 object-top opacity-80"
            />
          </div>
          <div>
            <div className="font-bold text-slate-200 text-sm flex items-center gap-2">
              AI Tutor Luna
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/80"></span>
            </div>
          </div>
        </div>
      </div>

      {/* 채팅 로그 */}
      <div
        ref={chatLogRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        <AnimatePresence mode="popLayout">
          {/* 기존 메시지 및 특수 카드 통합 렌더링 */}
          {chatHistory.map((msg, idx) => {
            const key = `${idx}-${msg.timestamp || idx}`;

            // 1. 특수 카드 타입 처리
            if (msg.type === 'curriculum_card') {
              return (
                <CurriculumCard
                  key={key}
                  curriculum={msg.content}
                  onStepClick={handleCurriculumStepClick}
                />
              );
            }

            if (msg.type === 'roadmap_card') {
              return (
                <RoadmapCard
                  key={key}
                  roadmap={msg.content}
                  onViewDetail={handleRoadmapViewDetail}
                  onRegenerate={handleRoadmapRegenerate}
                  onStart={handleRoadmapStart}
                />
              );
            }

            if (msg.type === 'path_selection_card') {
              return (
                <PathConsultation
                  key={key}
                  data={msg.content}
                  onSelectShort={handleConsultShort}
                  onSelectMedium={handleConsultMedium}
                  onSelectLong={handleConsultLong}
                  onRegenerate={handleConsultRegenerate}
                />
              );
            }

            if (msg.type === 'topic_options_card') {
              return (
                <TopicOptionsSelector
                  key={key}
                  topics={msg.content}
                  onSelect={handleSelectTopic}
                />
              );
            }

            if (msg.type === 'learning_type_card') {
              return (
                <LearningTypeSelector
                  key={key}
                  topic={msg.content}
                  onSelectRoadmap={handleSelectRoadmap}
                  onSelectCurriculum={handleSelectCurriculum}
                />
              );
            }

            // 2. 일반 채팅 메시지 (시스템, AI, User, completion_card, error_retry 등)
            return (
              <ChatMessage
                key={key}
                message={msg}
                onAction={
                  msg.type === 'completion_card' ? {
                    onReview: handleReview,
                    onPractice: handlePractice,
                    onNext: handleNextLesson,
                    isRoadmapMode: isRoadmapMode,
                    isLastLesson: msg.isLastLesson !== undefined ? msg.isLastLesson : isLastLesson
                  } : msg.type === 'error_retry' ? {
                    onRetry: () => handleRetry(msg)
                  } : undefined
                }
              />
            );
          })}

          {/* 로딩 인디케이터 - 스트리밍 중이거나 로딩 중일 때 표시 */}
          {(isStreaming || isLoading) && (
            <div className="flex flex-col gap-1 items-start max-w-full">
              {/* [Fix] 로딩 메시지는 말풍선 밖(위)에 작게 표시 -> 통일감 부여 */}
              {loadingMessage && !streamingContent && (
                <div className="text-[10px] text-slate-500 ml-3 animate-pulse">
                  {loadingMessage}
                </div>
              )}

              <ChatMessage
                key="ongoing-ai-response"
                message={{
                  role: 'ai',
                  content: streamingContent || <TypingIndicator /> // 메시지 없이 도트만 전달
                }}
                isStreaming={!!streamingContent}
              />
            </div>
          )}
        </AnimatePresence>


      </div>

      {/* 채팅 입력 - Floating Style */}
      <div className="px-4 pb-3 pt-2 bg-transparent relative">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (chatInputValue?.trim() && !isLoading && !isStreaming && !localInputLocked && username) {
              handleSubmit(chatInputValue);
            }
          }}
          className="relative flex items-end w-full"
        >
          <textarea
            ref={textareaRef}
            value={chatInputValue || ''}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (chatInputValue?.trim() && !isLoading && !isStreaming && !localInputLocked && username) {
                  handleSubmit(chatInputValue);
                }
              }
            }}
            rows={1}
            placeholder={!username ? "로그인이 필요합니다." : (localInputLocked ? localPlaceholder : "궁금한 것을 물어보세요!")}
            disabled={isLoading || isStreaming || localInputLocked || !username}
            className="flex-1 resize-none overflow-hidden max-h-40 bg-[#2f2f2f] text-slate-100 placeholder-slate-400 rounded-3xl py-[13px] pl-6 pr-12 text-sm focus:outline-none focus:ring-0 focus:bg-[#383838] border border-transparent shadow-lg transition-all duration-200"
            style={{ minHeight: '46px' }}
          />
          <button
            type="submit"
            disabled={isLoading || isStreaming || localInputLocked || !chatInputValue?.trim() || !username}
            className={clsx(
              "absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200",
              !chatInputValue?.trim() || isLoading || isStreaming || localInputLocked || !username
                ? "bg-transparent text-slate-500"
                : "bg-white text-black shadow-md hover:bg-slate-200 active:scale-95"
            )}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}
